import history from '@history';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import {
  applicationApi,
  applicationChangeApi,
  applicationV2Api,
  automatedActionApi,
  checklistsApi,
  companyApi,
  loanApplicationCommonInfoApi,
  loanApplicationsApi,
  loanChecklistsApi,
} from 'app/services/api';
import _ from 'lodash';
import { normalize, schema } from 'normalizr';

import { ChecklistExecutionSnapshotTargetType, ChecklistType } from 'constants/checklists';
import { FeatureFlag } from 'constants/featureFlags';
import {
  EscrowRequirementType,
  LOAN_APPLICATION_ACTION_NAME_PREFIX,
  LoanApplicationEscrowRequirementChangesMessage,
  LoanApplicationTabRouteParam,
  PricingStatusConfigurationType,
} from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { mapFlatChecklistResultsToLoanSnapshots } from 'utils/checklistFlatStoreAdapter';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { showMessageAction } from 'store/global/actions';
import { selectFeatureFlagByKey } from 'store/global/selectors';

import {
  selectApplicationIdFromDeal,
  selectCommonInfoLoanApplication,
  selectLoanPricingEngineType,
} from '../selectors';

import {
  cleanProtectedLoanChangesHistoryAction,
  deleteLoanChangesHistoryAction,
} from './loanChangesHistory.actions';
import { updatePricingStatusAction } from './pricingStatus.actions';

export const LOAN_APPLICATION_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  LOAN_APPLICATION_ACTION_NAME_PREFIX,
  {
    GET_LOAN_APPLICATION_COMMON_INFO_BY_APPLICATION_ID:
      'GET_LOAN_APPLICATION_COMMON_INFO_BY_APPLICATION_ID',
    GET_LOAN_APPLICATION_COMMON_INFO_REJECTED_STATUS:
      'GET_LOAN_APPLICATION_COMMON_INFO_REJECTED_STATUS',
    GET_LOAN_APPLICATION_COMMON_LOCK_INFO_BY_APPLICATION_ID:
      'GET_LOAN_APPLICATION_COMMON_LOCK_INFO_BY_APPLICATION_ID',
    GET_LOAN_APPLICATION_COMMON_LOCK_INFO_REJECTED_STATUS:
      'GET_LOAN_APPLICATION_COMMON_LOCK_INFO_REJECTED_STATUS',
    SET_LOAN_APPLICATION_CHANNEL_INFO: 'SET_LOAN_APPLICATION_CHANNEL_INFO',
    SAVE_LOAN_APPLICATION_CHANGES: 'SAVE_LOAN_APPLICATION_CHANGES',
    SAVE_LOAN_APPLICATION_CHANGES_PENDING_STATUS: 'SAVE_LOAN_APPLICATION_CHANGES_PENDING_STATUS',
    SAVE_LOAN_APPLICATION_CHANGES_REJECTED_STATUS: 'SAVE_LOAN_APPLICATION_CHANGES_REJECTED_STATUS',
    EDIT_LOAN_PROPERTY_RELATED_BY_APPLICATION_ID: 'EDIT_LOAN_PROPERTY_RELATED_BY_APPLICATION_ID',
    CANCEL_LOAN_CHANGES: 'CANCEL_LOAN_CHANGES',
    CANCEL_LOAN_CHANGES_PENDING_STATUS: 'CANCEL_LOAN_CHANGES_PENDING_STATUS',
    CANCEL_LOAN_CHANGES_REJECTED_STATUS: 'CANCEL_LOAN_CHANGES_REJECTED_STATUS',
    GENERATE_FORM: 'GENERATE_FORM',
    CANCEL_SENDING_BUNDLE: 'CANCEL_SENDING_BUNDLE',
    RE_RUN_MANUAL_AUS: 'RE_RUN_MANUAL_AUS',
    UPDATE_ROOT_AUS_RELATED_FIELDS: 'UPDATE_ROOT_AUS_RELATED_FIELDS',
    CREATE_LOAN_CLONE: 'CREATE_LOAN_CLONE',
    SEND_NOI: 'SEND_NOI',
    GET_CHECKLIST_RESULTS: 'GET_CHECKLIST_RESULTS',
    GET_COMPLETENESS_CHECKLIST_IDS: 'GET_COMPLETENESS_CHECKLIST_IDS',
    GET_COMPLETENESS_DESIGNATION: 'GET_COMPLETENESS_DESIGNATION',
    OPTIMISTICALLY_OVERRIDE_CHECKLIST_ITEM: 'OPTIMISTICALLY_OVERRIDE_CHECKLIST_ITEM',
    TRIGGER_OPTIONAL_CHECKLISTS_RECALCULATION: 'TRIGGER_OPTIONAL_CHECKLISTS_RECALCULATION',
    GET_RELATED_LOANS: 'GET_RELATED_LOANS',
  },
);

const loanApplicationChecklistExecutionSnapshotsById = new schema.Entity(
  'checklistResults',
  {},
  {
    // Keyed by (checklistId, targetId) rather than checklistId alone (DEV-4684): a Borrower-scoped
    // completeness checklist runs once PER BORROWER and shares its checklistId across every
    // borrower, so keying by checklistId alone collides and only the last-normalized borrower's
    // snapshot survives. A LoanApplication-scoped snapshot's targetId is the applicationId, so its
    // key is unchanged in shape and stays unique.
    idAttribute: ({ target }) => `${target.checklistId}:${target.targetId}`,
    mergeStrategy: (firstOpt, secondOpt) => {
      if (_.camelCase(secondOpt.target.executionType) === ChecklistType.PENDING_CHANGES) {
        return secondOpt;
      }

      return firstOpt;
    },
  },
);

export const showEscrowRequiredChangesMessageAction = data => (dispatch, getState) => {
  const state = getState();

  const isEscrowRequirementsChangesMessageHidden = selectFeatureFlagByKey(
    FeatureFlag.HideEscrowRequirementsChangesMessage,
  )(state);

  if (isEscrowRequirementsChangesMessageHidden) {
    return;
  }

  const applicationId = selectApplicationIdFromDeal(state);
  const { escrowRequirement } = selectCommonInfoLoanApplication(state);
  const escrowRequired = escrowRequirement === EscrowRequirementType.REQUIRED;
  const nextEscrowRequired = data?.escrowRequired;
  const isEscrowRequiredChanged = escrowRequired !== nextEscrowRequired;

  if (isEscrowRequiredChanged) {
    const message = nextEscrowRequired
      ? LoanApplicationEscrowRequirementChangesMessage.ESCROW_REQUIRED
      : LoanApplicationEscrowRequirementChangesMessage.ESCROW_NOT_REQUIRED;

    dispatch(showMessageAction({ message, variant: 'success' }));

    LoansPaths.applicationManagement.navigateWith(history.push, {
      [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
      [LOANS_ROUTING_PARAMS.tab]: LoanApplicationTabRouteParam.PROPERTY_AND_LOAN,
    });
  }
};

const getLoanApplicationCommonInfoByApplicationIdAction = params =>
  catchAsyncActionError(({ applicationId, isEscrowChangesMessageVisible }) => async dispatch => {
    const { data } = await loanApplicationCommonInfoApi.getApplicationCommonInfo({
      applicationId,
    });

    if (isEscrowChangesMessageVisible) {
      dispatch(showEscrowRequiredChangesMessageAction(data));
    }

    return dispatch({
      type: LOAN_APPLICATION_ACTION_TYPE.GET_LOAN_APPLICATION_COMMON_INFO_BY_APPLICATION_ID,
      payload: data,
    });
  })({
    ...params,
    rejectedAction: () => ({
      type: LOAN_APPLICATION_ACTION_TYPE.GET_LOAN_APPLICATION_COMMON_INFO_REJECTED_STATUS,
    }),
  });

const getLoanApplicationCommonLockInfoByApplicationIdAction = params =>
  catchAsyncActionError(({ applicationId }) => async dispatch => {
    const { data } = await loanApplicationCommonInfoApi.getApplicationLockInfo({
      applicationId,
    });

    return dispatch({
      type: LOAN_APPLICATION_ACTION_TYPE.GET_LOAN_APPLICATION_COMMON_LOCK_INFO_BY_APPLICATION_ID,
      payload: data,
    });
  })({
    ...params,
    rejectedAction: () => ({
      type: LOAN_APPLICATION_ACTION_TYPE.GET_LOAN_APPLICATION_COMMON_LOCK_INFO_REJECTED_STATUS,
    }),
  });

export const getLoanApplicationChannelInfoByApplicationIdAction = catchAsyncActionError(
  ({ applicationId, companyId }) =>
    async dispatch => {
      const { data } = await loanApplicationsApi.getLoanApplication({
        companyId,
        applicationId,
      });

      const { channelId = null, channelType = null, subCompanyId = null } = data?.application ?? {};

      return dispatch({
        type: LOAN_APPLICATION_ACTION_TYPE.SET_LOAN_APPLICATION_CHANNEL_INFO,
        payload: { channelId, channelType, subCompanyId },
      });
    },
);

export const getLoanApplicationCommonDataByApplicationIdAction =
  ({ applicationId, isEscrowChangesMessageVisible }) =>
  async dispatch =>
    Promise.all([
      dispatch(
        getLoanApplicationCommonInfoByApplicationIdAction({
          applicationId,
          isEscrowChangesMessageVisible,
        }),
      ),
      dispatch(getLoanApplicationCommonLockInfoByApplicationIdAction({ applicationId })),
    ]);

export const editLoanPropertyRelatedAction = actionData => async dispatch => {
  const { data } = await applicationV2Api.editPropertyRelated(actionData);

  return dispatch({
    type: LOAN_APPLICATION_ACTION_TYPE.EDIT_LOAN_PROPERTY_RELATED_BY_APPLICATION_ID,
    payload: data,
  });
};

export const cancelSendingBundleAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await applicationApi.cancelSendingBundle(params);

  return dispatch({
    type: LOAN_APPLICATION_ACTION_TYPE.CANCEL_SENDING_BUNDLE,
    payload: data,
  });
});

const saveApplicationChangesRejectedAction = () => dispatch => {
  dispatch(cleanProtectedLoanChangesHistoryAction());

  return dispatch({
    type: LOAN_APPLICATION_ACTION_TYPE.SAVE_LOAN_APPLICATION_CHANGES_REJECTED_STATUS,
  });
};

export const saveApplicationChangesAction = params =>
  catchAsyncActionError(({ isInitiatedByLeadStage, ...params }) => async (dispatch, getState) => {
    const { data } = await applicationChangeApi.saveApplicationChanges(params);

    if (!isInitiatedByLeadStage) {
      dispatch(showEscrowRequiredChangesMessageAction(data));
    }

    dispatch(deleteLoanChangesHistoryAction());

    if (data.metadata?.isSaved === false && params.applicationId) {
      const state = getState();
      const companyId = selectMasterCompanyId(state);
      const pricingEngineType = selectLoanPricingEngineType(state);

      dispatch(
        updatePricingStatusAction({
          companyId,
          applicationId: params.applicationId,
          pricingEngineType,
          include: [PricingStatusConfigurationType.PROGRAM_CHANGE_CONFIGURATION],
        }),
      );
    }

    return dispatch({
      type: LOAN_APPLICATION_ACTION_TYPE.SAVE_LOAN_APPLICATION_CHANGES,
      payload: data,
    });
  })({
    ...params,
    pendingAction: () => ({
      type: LOAN_APPLICATION_ACTION_TYPE.SAVE_LOAN_APPLICATION_CHANGES_PENDING_STATUS,
    }),
    rejectedAction: saveApplicationChangesRejectedAction,
    silent: true,
  });

const cancelApplicationChangesRejectedAction = () => dispatch => {
  dispatch(cleanProtectedLoanChangesHistoryAction());

  return dispatch({
    type: LOAN_APPLICATION_ACTION_TYPE.CANCEL_LOAN_CHANGES_REJECTED_STATUS,
  });
};

export const cancelApplicationChangesAction = params =>
  catchAsyncActionError(({ applicationId, authors = null }) => async dispatch => {
    const { data } = await applicationChangeApi.cancelApplicationChanges({
      applicationId,
      authors,
    });

    dispatch(deleteLoanChangesHistoryAction());

    return dispatch({
      type: LOAN_APPLICATION_ACTION_TYPE.CANCEL_LOAN_CHANGES,
      payload: data,
    });
  })({
    ...params,
    pendingAction: () => ({
      type: LOAN_APPLICATION_ACTION_TYPE.CANCEL_LOAN_CHANGES_PENDING_STATUS,
    }),
    rejectedAction: cancelApplicationChangesRejectedAction,
  });

export const updateRootAusRelatedFieldsAction = data => ({
  type: LOAN_APPLICATION_ACTION_TYPE.UPDATE_ROOT_AUS_RELATED_FIELDS,
  payload: data,
});

export const createLoanCloneAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await applicationApi.createLoanClone(params);

  return dispatch({
    type: LOAN_APPLICATION_ACTION_TYPE.CREATE_LOAN_CLONE,
    payload: data,
  });
});

export const sendNoiAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await automatedActionApi.sendNoi(params);

  return dispatch({
    type: LOAN_APPLICATION_ACTION_TYPE.SEND_NOI,
    payload: data,
  });
});

// FH-1931: behind `ReadChecklistsFromFlatStore`, calls the FH-1829 flat-store lean read directly
// against Loans instead of the SkynetAPI legacy snapshots proxy, mapping its response back through
// `mapFlatChecklistResultsToLoanSnapshots` into the exact `ChecklistExecutionSnapshotModel[]` shape
// `normalize` already expects below — so every downstream reducer/selector is untouched by the flag.
const getChecklistExecutionSnapshotsData = async (
  applicationId,
  params,
  isFlatStoreReadEnabled,
) => {
  if (isFlatStoreReadEnabled) {
    const { data } = await checklistsApi.getFlatChecklistResults({
      targetEntityIds: [applicationId],
      targetEntityType: ChecklistExecutionSnapshotTargetType.LOAN_APPLICATION,
      withResults: true,
      withItems: true,
      // The loan-editor mount read: visible checklists come back head-only (items load on click).
      // Loans no longer infers this from the missing `targets`, so it must be asked for.
      mountRead: true,
    });

    return mapFlatChecklistResultsToLoanSnapshots(data);
  }

  const { data } = await checklistsApi.getChecklistsExecutionSnapshots(params);

  return data.data;
};

export const getChecklistsExecutionSnapshotsAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const isFlatStoreReadEnabled = selectFeatureFlagByKey(FeatureFlag.ReadChecklistsFromFlatStore)(
      getState(),
    );
    const snapshots = await getChecklistExecutionSnapshotsData(
      params.applicationId,
      params,
      isFlatStoreReadEnabled,
    );

    const resultsAction = dispatch({
      type: LOAN_APPLICATION_ACTION_TYPE.GET_CHECKLIST_RESULTS,
      payload: normalize(snapshots, [loanApplicationChecklistExecutionSnapshotsById]).entities
        .checklistResults,
    });

    // FH-3004: the flat mount payload above already carries every checklist's `isHidden` head, so
    // populate the completeness-ids slice from IT here instead of the loan-editor mount effect
    // firing a second `getCompletenessChecklistIdsAction` request (skipped there when this flag is
    // on — see LoanApplicationPage).
    if (isFlatStoreReadEnabled) {
      dispatch({
        type: LOAN_APPLICATION_ACTION_TYPE.GET_COMPLETENESS_CHECKLIST_IDS,
        payload: _.uniq(
          snapshots
            .filter(snapshot => snapshot.status?.result?.checklist?.isHidden)
            .map(snapshot => snapshot.target?.checklistId),
        ),
      });
    }

    return resultsAction;
  },
);

// FH-1931: behind `ReadChecklistsFromFlatStore`, resolve the hidden-checklist completeness ids from the
// FH-1829 flat-store lean read instead of the legacy loan-scoped `checklist-result` read — so the flag
// swap covers THIS read too and no legacy call fires when it is on. The flat head carries `isHidden` and
// the lane-invariant `targetChecklistId` (populated in preview, items=false), and both lanes
// (Saved/PendingChanges) share that id — so dedup. Legacy branch unchanged (includePendingChanges=false
// already yields one row per checklist).
const getCompletenessChecklistIdsData = async (applicationId, isFlatStoreReadEnabled) => {
  if (isFlatStoreReadEnabled) {
    const { data } = await checklistsApi.getFlatChecklistResults({
      targetEntityIds: [applicationId],
      targetEntityType: ChecklistExecutionSnapshotTargetType.LOAN_APPLICATION,
      withResults: false,
      withItems: false,
    });

    return _.uniq(data.filter(result => result.isHidden).map(result => result.targetChecklistId));
  }

  const { data } = await checklistsApi.getLoanChecklistResults({
    applicationId,
    includeItems: false,
    includePendingChanges: false,
  });

  return data.filter(result => result.checklist?.isHidden).map(result => result.checklist.id);
};

/**
 * Loads the set of checklist ids that drive the in-editor "Needed" completeness highlighting
 * (DEV-4603). Only HIDDEN checklists are completeness decisioning engines — visible business/test
 * checklists (e.g. `AK_TEST_CHECKLIST`, `777 FE test`) must NOT light filled fields as "Needed" even
 * when they carry a `fieldRef`. Both the flat read and the legacy loan-scoped `checklist-result` read
 * expose each checklist's `isHidden` flag under the loan-view claim (unlike the admin-only `/checklists`
 * list), so a plain LO can resolve the completeness set. Items are not needed here — only the checklist
 * metadata — so this is a lightweight read alongside the execution-snapshot fetch.
 */
export const getCompletenessChecklistIdsAction = catchAsyncActionError(
  ({ applicationId }) =>
    async (dispatch, getState) => {
      const isFlatStoreReadEnabled = selectFeatureFlagByKey(
        FeatureFlag.ReadChecklistsFromFlatStore,
      )(getState());

      const completenessChecklistIds = await getCompletenessChecklistIdsData(
        applicationId,
        isFlatStoreReadEnabled,
      );

      return dispatch({
        type: LOAN_APPLICATION_ACTION_TYPE.GET_COMPLETENESS_CHECKLIST_IDS,
        payload: completenessChecklistIds,
      });
    },
);

/**
 * Loads the company's DESIGNATED completeness checklist id (`Companies.CompletenessChecklistId`,
 * set via the company-settings picker — DEV-4603). When set, exactly that checklist drives the
 * in-editor "Needed" highlighting; when null (the default) the highlighting falls back to the loan's
 * hidden completeness checklist(s) — the global master. The read is `skipForbiddenError` so a
 * loan-editor user without `company.view` degrades silently to that hidden-checklist default rather
 * than surfacing a 403.
 */
export const getCompletenessDesignationAction = catchAsyncActionError(
  ({ companyId }) =>
    async dispatch => {
      const { data } = await companyApi.getCompany({ companyId, skipForbiddenError: true });

      return dispatch({
        type: LOAN_APPLICATION_ACTION_TYPE.GET_COMPLETENESS_DESIGNATION,
        payload: data?.completenessChecklistId ?? null,
      });
    },
);

/**
 * Optimistically marks a single checklist item as overridden in the
 * `loanApplicationChecklistResults` slice so the field-completeness indicator
 * flips to "Overridden" on the click (DEV-4468) — instead of waiting for the
 * async server re-eval + the SignalR `ChecklistExecutionSnapshotsChanged` push.
 * The eventual push reconciles the slice with server truth.
 *
 * `targetId` is forwarded so the reducer flips the item on the RIGHT snapshot —
 * per-borrower checklists share one checklistId across borrowers, keyed by
 * `${checklistId}:${targetId}`; without it the optimistic flip landed on the
 * first borrower's snapshot and the viewed field never updated (DEV-4728).
 *
 * `isOverridden: false` optimistically REVERTS the override — the toast's Undo
 * (DEV-5146) — flipping the indicator back to "Needed" while the remove-override
 * request round-trips.
 */
export const optimisticallyOverrideChecklistItemAction = ({
  checklistId,
  itemId,
  targetId,
  isOverridden = true,
}) => ({
  type: LOAN_APPLICATION_ACTION_TYPE.OPTIMISTICALLY_OVERRIDE_CHECKLIST_ITEM,
  payload: { checklistId, itemId, targetId, isOverridden },
});

export const triggerOptionalChecklistRecalculation = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await loanChecklistsApi.triggerOptionalChecklistRecalculation(params);

    return dispatch({
      type: LOAN_APPLICATION_ACTION_TYPE.TRIGGER_OPTIONAL_CHECKLISTS_RECALCULATION,
      payload: data,
    });
  },
);

export const getRelatedLoansAction = catchAsyncActionError(
  ({ applicationId }) =>
    async dispatch => {
      const { data } = await applicationV2Api.getRelatedLoans({ applicationId });

      return dispatch({
        type: LOAN_APPLICATION_ACTION_TYPE.GET_RELATED_LOANS,
        payload: data,
      });
    },
);
